import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  FormOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import Spinner from '../../common/Spinner';
import { getTournamentBracketRequest } from '../../../api/requests/tournaments';
import {
  BracketDetailsData,
  BracketMatchupDto,
  ParticipantDto,
  RoundDto,
  TournamentMatchUpdatePayload,
} from '../../../types/types';
import { WarningFilled } from '@ant-design/icons';
import { Button } from 'antd';
import MatchVenueDetailsModal from './MatchVenueDetailsModal';
import {
  buildMatchScorePath,
  buildDoublesPlayersDetailsPath,
} from '../../../routes/routePaths';
import { TrophyIcon } from './SinglesBrackets';
import { parseScoreData } from '../../../utils/eventUtils';
import { useEventContext } from '../../../contexts/EventContext';
import CustomButton from '../../common/Button';

// Match Card Component
const MatchCard: React.FC<{
  index: number;
  match: BracketMatchupDto;
  onPress?: (match: BracketMatchupDto) => void;
  selectedRound: number;
  isStale?: boolean;
}> = ({ index, match, onPress, selectedRound, isStale }) => {
  const navigate = useNavigate();
  const { sportName } = useEventContext();
  const { eventId, tournamentId } = useParams();

  const renderWinnerStar = (participantId?: number | null) => {
    if (
      !match?.winner ||
      match.winner.participantId == null ||
      participantId == null
    ) {
      return null;
    }

    return match.winner.participantId === participantId ? <TrophyIcon /> : null;
  };

  const handleMatchScore = ({ id }: { id: number }) => {
    if (isStale) return;
    const path = buildMatchScorePath(Number(eventId), Number(tournamentId), id);
    navigate(`${path}?round=${selectedRound}`);
  };

  const getTeamScore = (
    participantScoreData?: string | null,
  ): string | null => {
    if (!participantScoreData) return null;
    let scoreData: any;
    try {
      scoreData = parseScoreData(participantScoreData, sportName);
    } catch {
      return null;
    }
    if (!scoreData || typeof scoreData !== 'object') return null;
    if (Array.isArray(scoreData.sets) && scoreData.sets.length > 0) {
      return scoreData.sets.join(' | ');
    }
    if (Array.isArray(scoreData.games) && scoreData.games.length > 0) {
      return scoreData.games.join(' | ');
    }

    return null;
  };

  const getParticipantNames = (participant?: ParticipantDto) => {
    if (!participant || !participant.participantName) {
      return 'TBD';
    }
    return `${participant.participantName}  ${
      participant.partnerName ? `& ${participant.partnerName}` : ''
    }`;
  };

  const team1Score = getTeamScore(match?.match?.participant1ScoreData);
  const team2Score = getTeamScore(match?.match?.participant2ScoreData);

  return (
    <MatchContainer>
      <MatchTitle>Match {index + 1}</MatchTitle>
      <MatchCardWrapper onClick={() => handleMatchScore({ id: match.matchId })}>
        <MatchInfo>
          <TeamsSection>
            <TeamContainer>
              <TeamNumberCircle>
                <TeamNumber>1</TeamNumber>
              </TeamNumberCircle>
              <TeamName>{getParticipantNames(match.participant1)}</TeamName>
              {team1Score && <TeamScore>{team1Score}</TeamScore>}
              {renderWinnerStar(match.participant1?.participantId)}
            </TeamContainer>
            <TeamContainer>
              <TeamNumberCircle>
                <TeamNumber>2</TeamNumber>
              </TeamNumberCircle>
              <TeamName>{getParticipantNames(match.participant2)}</TeamName>
              {team2Score && <TeamScore>{team2Score}</TeamScore>}
              {renderWinnerStar(match.participant2?.participantId)}
            </TeamContainer>
          </TeamsSection>
          <ActionContainer>
            <VenueUpdateIcon
              onClick={(e) => {
                e.stopPropagation();
                onPress && onPress(match);
              }}
            />
            <StatusContainer>
              <StatusText>{match.match?.statusName}</StatusText>
            </StatusContainer>
          </ActionContainer>
        </MatchInfo>
        <TimeLocation>
          <InfoRow>
            <StyledCalendarOutlined />
            <InfoText>
              {dayjs(match.match?.scheduledStartTime).format(
                'DD MMMM YYYY h:mm A',
              )}
            </InfoText>
          </InfoRow>
          <InfoRow>
            <StyledEnvironmentOutlined />
            <LocationText>{match.match?.matchLocation}</LocationText>
          </InfoRow>
        </TimeLocation>
      </MatchCardWrapper>
    </MatchContainer>
  );
};

// Main Component
const DoublesBrackets: React.FC = () => {
  const navigate = useNavigate();
  const { eventId, tournamentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const roundFromUrl = Number(searchParams.get('round')) || 1;

  const [selectedRound, setSelectedRound] = useState<number>(roundFromUrl);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bracketDetails, setBracketDetails] = useState<BracketDetailsData>({
    totalMatchups: 0,
    totalCompletedMatchups: 0,
    rounds: [],
  });
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<
    TournamentMatchUpdatePayload | undefined
  >(undefined);

  const fetchBracketDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getTournamentBracketRequest(Number(tournamentId));
      if (response?.isSuccess) {
        setBracketDetails(response.value);
      }
    } catch (error) {
      // handle error (snackbar, etc.)
      console.error('Error fetching bracket details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBracketDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setUpdateModalOpen(false);
  };

  const handleMatchUpdate = (match: BracketMatchupDto) => {
    setUpdateModalOpen(true);
    setInitialValues({
      facilityId: match.match?.facilityId,
      tournamentMatchId: match.match?.tournamentMatchId || 0,
      tournamentMatchLocation: match.match?.matchLocation || '',
      scheduledStartTime: match.match?.scheduledStartTime || '',
    });
  };

  const roundsList = useMemo(() => {
    return (
      bracketDetails?.rounds?.map((r: RoundDto) => ({
        id: r.round,
        name: r.roundName,
      })) || []
    );
  }, [bracketDetails]);

  const isStale = Boolean(bracketDetails?.bracketIsStale);
  const unpairedLeftoverCount = bracketDetails?.unpairedLeftoverCount ?? 0;
  const registrationOpen = Boolean(bracketDetails?.registrationWindowOpen);
  // Scoring is blocked while registration is still open or the bracket is stale.
  const scoringBlocked = isStale || registrationOpen;

  // A match has started once any matchup is in progress or completed.
  const isAnyMatchStarted = useMemo(() => {
    return (
      bracketDetails?.rounds?.some((round) =>
        round.matchups?.some(
          (m: any) =>
            m.match?.statusName === 'InProgress' ||
            m.match?.statusName === 'Completed',
        ),
      ) ?? false
    );
  }, [bracketDetails]);

  // Regeneration is allowed any time before a match starts — to absorb new
  // registrations (stale) or simply to reshuffle / change the format.
  const canRegenerate = !isAnyMatchStarted;

  const handleRegenerate = () => {
    navigate(
      `/event-management/${eventId}/${buildDoublesPlayersDetailsPath(
        Number(tournamentId),
      )}`,
    );
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <MainContainer>
      {registrationOpen && (
        <StaleBanner>
          <StaleBannerIcon />
          <StaleBannerText>
            Registration is still open for this tournament. Close registration
            before entering scores — otherwise players registering now can no
            longer be added once a match begins.
          </StaleBannerText>
        </StaleBanner>
      )}
      {isStale && (
        <StaleBanner>
          <StaleBannerIcon />
          <StaleBannerText>
            New registrations have been received and unpaired players can now be
            paired. The bracket is outdated — regenerate it to include everyone
            before starting matches or entering scores.
          </StaleBannerText>
          <Button type="primary" size="small" onClick={handleRegenerate}>
            Regenerate Bracket
          </Button>
        </StaleBanner>
      )}
      {unpairedLeftoverCount > 0 && (
        <InfoBanner>
          <InfoBannerText>
            {`${unpairedLeftoverCount} ${
              unpairedLeftoverCount === 1 ? 'player is' : 'players are'
            } unpaired and not included in the bracket.`}
          </InfoBannerText>
        </InfoBanner>
      )}
      <RoundsScrollContainer>
        <RoundsWrapper>
          {roundsList.map((round) => {
            const isActive = selectedRound === round.id;
            return (
              <RoundButton
                key={round.id}
                active={isActive}
                onClick={() => {
                  setSelectedRound(round.id);
                  setSearchParams({ round: round.id.toString() });
                }}
              >
                <RoundButtonText active={isActive}>
                  {round.name}
                </RoundButtonText>
              </RoundButton>
            );
          })}
        </RoundsWrapper>
        {!isStale && canRegenerate && (
          <CustomButton type="primary" size="small" onClick={handleRegenerate}>
            Regenerate Bracket
          </CustomButton>
        )}
      </RoundsScrollContainer>

      <BodyContainer>
        <ScrollContainer>
          {bracketDetails?.rounds
            ?.find((round) => round.round === selectedRound)
            ?.matchups?.map((match, index) => (
              <MatchCard
                key={`${selectedRound}-${match.matchId}-${index}`}
                index={index}
                match={match}
                onPress={handleMatchUpdate}
                selectedRound={selectedRound}
                isStale={scoringBlocked}
              />
            )) || (
            <EmptyState>
              <EmptyText>No matches found for this round</EmptyText>
            </EmptyState>
          )}
        </ScrollContainer>
        {updateModalOpen && (
          <MatchVenueDetailsModal
            visible={updateModalOpen}
            handleClose={handleClose}
            initialValues={initialValues}
            onUpdate={fetchBracketDetails}
          />
        )}
      </BodyContainer>
    </MainContainer>
  );
};

// Styled Components
const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${COLORS.white.primary};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const StaleBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin: 12px 16px 0;
  padding: 10px 14px;
  border-radius: 6px;
  background: #fff4e5;
  border: 1px solid ${COLORS.orange.primary};
`;

const StaleBannerIcon = styled(WarningFilled)`
  color: ${COLORS.orange.primary};
  font-size: ${TEXT_SIZES.LG}px;
  flex-shrink: 0;
`;

const StaleBannerText = styled.div`
  flex: 1;
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  line-height: 1.4;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin: 12px 16px 0;
  padding: 10px 14px;
  border-radius: 6px;
  background: ${COLORS.skyBlue.light};
  border: 1px solid ${COLORS.skyBlue.primary};
`;

const InfoBannerText = styled.div`
  flex: 1;
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  line-height: 1.4;
`;

const RoundsScrollContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  padding: 8px;
  background-color: ${COLORS.white.primary};
  justify-content: space-between;
`;

const RoundsWrapper = styled.div`
  display: flex;
  gap: 10px;
  min-width: min-content;
`;

const RoundButton = styled.button<{ active: boolean }>`
  background-color: ${(props) =>
    props.active ? COLORS.blue.primary : COLORS.grey.extraLight};
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.active ? COLORS.black.primary : COLORS.grey.lightMedium};
  }
`;

const RoundButtonText = styled.span<{ active: boolean }>`
  color: ${(props) =>
    props.active ? COLORS.white.primary : COLORS.black.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
`;

const BodyContainer = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
`;

const MatchContainer = styled.div`
  margin-bottom: 16px;
  cursor: pointer;
  width: 45%;
`;

const MatchTitle = styled.div`
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  margin-bottom: 8px;
  text-align: center;
  color: ${COLORS.black.primary};
`;

const MatchCardWrapper = styled.div`
  background-color: ${COLORS.skyBlue.light};
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${COLORS.skyBlue.primary};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const MatchInfo = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const TeamsSection = styled.div`
  flex: 1;
`;

const TeamContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TeamNumberCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: ${COLORS.blue.primary};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  flex-shrink: 0;
`;

const TeamNumber = styled.span`
  color: ${COLORS.white.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const TeamName = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  color: ${COLORS.black.primary};
`;

const TeamScore = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.skyBlue.primary};
  margin-left: 12px;
`;

const StatusContainer = styled.div`
  background-color: ${COLORS.blue.primary};
  padding: 4px 10px;
  border-radius: 8px;
  margin-left: 8px;
  flex-shrink: 0;
`;

const StatusText = styled.span`
  color: ${COLORS.white.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  white-space: nowrap;
`;

const TimeLocation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 4px;
  min-height: 50px;
`;

const InfoText = styled.span`
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.primary};
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const LocationText = styled.span`
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.primary};

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const EmptyText = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.grey.medium};
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
`;

const VenueUpdateIcon = styled(FormOutlined)`
  font-size: ${TEXT_SIZES.MD}px;
  cursor: pointer;
  z-index: 1;
`;

const StyledCalendarOutlined = styled(CalendarOutlined)`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.grey.medium};
`;

const StyledEnvironmentOutlined = styled(EnvironmentOutlined)`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.grey.medium};
`;
export default DoublesBrackets;
